/**
 * 🚗 VEHICLE SELECTOR COMPONENT
 * Advanced vehicle selection with filtering and search for admin booking creation
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Stack,
  Grid,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  CardActions,
} from '@mui/material';
import { Search, FilterList, Clear, CheckCircle, DirectionsCar, LocationOn } from '@mui/icons-material';
import { vehicleAPI } from '@/services/vehicleAPI';
import vehicleMetadataService, { VehicleType, VehicleBodyType } from '@/services/vehicleMetadataService';

interface VehicleSelectorProps {
  selectedVehicle: any | null;
  onSelectVehicle: (vehicle: any) => void;
  rentalOption?: 'self_drive_full_day' | 'self_drive_half_day' | 'with_driver_full_day' | 'with_driver_half_day';
  bookingType?: 'rental' | 'airport-transfer';
  airportId?: string; // For airport transfers - filter by airport
  zoneId?: string; // For airport transfers - filter by zone
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

// Cache outside component to avoid reloading
let cachedVehicleTypes: VehicleType[] | null = null;
let cachedBodyTypes: VehicleBodyType[] | null = null;
let cachedVehicles: any[] | null = null;
let metadataLoadPromise: Promise<void> | null = null;
let vehiclesLoadPromise: Promise<void> | null = null;

export const VehicleSelector: React.FC<VehicleSelectorProps> = ({
  selectedVehicle,
  onSelectVehicle,
  rentalOption,
  bookingType = 'rental',
  airportId,
  zoneId,
  label = 'Select Vehicle',
  required = true,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedVehicleType, setSelectedVehicleType] = useState('');
  const [selectedBodyType, setSelectedBodyType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('approved'); // Default to approved only
  const [selectedFuelType, setSelectedFuelType] = useState('');
  const [selectedTransmission, setSelectedTransmission] = useState('');

  // Filter options (will be populated from vehicles data and API)
  const [makes, setMakes] = useState<string[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [bodyTypes, setBodyTypes] = useState<VehicleBodyType[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [tiers, setTiers] = useState<string[]>([]);

  // Load metadata once on first open
  useEffect(() => {
    if (open && !metadataLoadPromise) {
      metadataLoadPromise = loadMetadata();
    }
  }, [open]);

  // Load vehicles when dialog opens, booking type changes, or backend filters change
  useEffect(() => {
    if (open) {
      loadVehicles();
    }
  }, [open, bookingType, selectedStatus, selectedFuelType, selectedTransmission, airportId, zoneId]);

  const loadMetadata = async () => {
    // Return cached data if available
    if (cachedVehicleTypes && cachedBodyTypes) {
      setVehicleTypes(cachedVehicleTypes);
      setBodyTypes(cachedBodyTypes);
      return;
    }

    try {

      const [typesResponse, bodyTypesResponse] = await Promise.all([
        vehicleMetadataService.getVehicleTypes(),
        vehicleMetadataService.getVehicleBodyTypes(),
      ]);


      if (typesResponse.success && typesResponse.data) {
        cachedVehicleTypes = typesResponse.data.vehicleTypes;
        setVehicleTypes(cachedVehicleTypes);
      } else {
      }

      if (bodyTypesResponse.success && bodyTypesResponse.data) {
        cachedBodyTypes = bodyTypesResponse.data.bodyTypes;
        setBodyTypes(cachedBodyTypes);
      } else {
      }
    } catch (error) {
      console.error('❌ Error loading vehicle metadata:', error);
    }
  };

  const extractFilterOptions = (vehicleList: any[]) => {
    const uniqueMakes = [...new Set(vehicleList.map((v: any) => {
      const vehicleData = v.vehicle || v;
      return vehicleData.make || vehicleData.vehicle_make;
    }).filter(Boolean))];

    const uniqueLocations = [...new Set(vehicleList.map((v: any) => {
      const hostData = v.host;
      return hostData?.city || hostData?.state || hostData?.location;
    }).filter(Boolean))];

    const uniqueCapacities = [...new Set(vehicleList.map((v: any) => {
      const vehicleData = v.vehicle || v;
      return vehicleData.seating_capacity || vehicleData.seatingCapacity
        ? `${vehicleData.seating_capacity || vehicleData.seatingCapacity} seats`
        : null;
    }).filter(Boolean))];

    setMakes(uniqueMakes as string[]);
    setLocations(uniqueLocations as string[]);
    setTiers(uniqueCapacities as string[]);
  };

  const loadVehicles = async () => {
    setLoading(true);
    setError(null);

    try {
      const startTime = performance.now();
      let vehicleList: any[] = [];

      if (bookingType === 'airport-transfer') {
        // Use dedicated airport pricing endpoint for airport transfers
        const response = await vehicleAPI.getVehiclesWithAirportPricing({
          hasPricing: true, // Only vehicles WITH airport pricing
          airportId: airportId || undefined, // Filter by airport if selected
          zoneId: zoneId || undefined, // Filter by zone if selected
          includePricingData: true, // Include full pricing details
          limit: 100,
        });


        // Get vehicle IDs to fetch full details (for photos and complete data)
        const vehicleIds = (response?.vehicles || []).map((v: any) => v.vehicleId);

        // Fetch full vehicle details to get photos and other data
        const fullVehiclesResponse = await vehicleAPI.getAdminVehicles({
          limit: 100,
          status: 'approved'
        });

        const fullVehiclesMap = new Map(
          (fullVehiclesResponse?.vehicles || []).map((v: any) => [v.id || v.vehicle_id, v])
        );

        // Transform airport pricing vehicle format to match regular format
        vehicleList = (response?.vehicles || []).map((v: any) => {
          const fullVehicle = fullVehiclesMap.get(v.vehicleId);

          // If we have full vehicle data, use it; otherwise create minimal object
          if (fullVehicle) {
            return {
              ...fullVehicle,
              hasAirportPricing: v.hasAirportPricing,
              airportPricingCount: v.airportPricingCount,
              airportPricings: v.pricings || [],
            };
          }

          // Fallback to minimal data if full vehicle not found
          return {
            id: v.vehicleId,
            vehicle_id: v.vehicleId,
            status: 'approved',
            hasAirportPricing: v.hasAirportPricing,
            airportPricingCount: v.airportPricingCount,
            airportPricings: v.pricings || [],
            vehicle: {
              make: v.vehicleMake,
              model: v.vehicleModel,
              year: v.vehicleYear,
              plate_number: v.plateNumber,
              vehicle_type: v.vehicleType,
            },
            host: {
              id: v.hostId,
              name: v.hostName,
              email: v.hostEmail,
            },
            photos: {
              count: 0,
              items: [],
            },
          };
        });

        const loadTime = performance.now() - startTime;
      } else {
        // Use regular admin vehicles endpoint for rentals
        const response = await vehicleAPI.getAdminVehicles({
          status: selectedStatus || 'approved',
          fuel_type: selectedFuelType || undefined,
          transmission: selectedTransmission || undefined,
          limit: 100,
          offset: 0
        });

        vehicleList = response?.vehicles || response?.data || [];

        const loadTime = performance.now() - startTime;
      }

      setVehicles(vehicleList);

      // OPTIMIZATION: Extract filters asynchronously to avoid blocking UI
      setTimeout(() => extractFilterOptions(vehicleList), 0);
    } catch (err) {
      console.error('❌ Error loading vehicles:', err);
      setError('Failed to load vehicles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedMake('');
    setSelectedVehicleType('');
    setSelectedBodyType('');
    setSelectedLocation('');
    setSelectedTier('');
    setSelectedStatus('approved'); // Reset to approved
    setSelectedFuelType('');
    setSelectedTransmission('');
    setPage(0);
  };

  // Helper to get approval status color and label
  const getStatusBadge = (status: string) => {
    const statusLower = (status || '').toLowerCase();

    if (statusLower === 'approved') {
      return { color: 'success' as const, label: 'APPROVED' };
    } else if (statusLower === 'pending_approval' || statusLower === 'pending') {
      return { color: 'warning' as const, label: 'PENDING' };
    } else if (statusLower === 'rejected') {
      return { color: 'error' as const, label: 'REJECTED' };
    } else if (statusLower === 'draft') {
      return { color: 'default' as const, label: 'DRAFT' };
    } else if (statusLower === 'unknown') {
      return { color: 'info' as const, label: 'UNKNOWN' };
    }
    // For any other status value, display it as-is
    return { color: 'default' as const, label: status ? status.toUpperCase() : 'N/A' };
  };

  const handleSelectVehicle = (vehicle: any) => {
    onSelectVehicle(vehicle);
    setOpen(false);
  };

  const handleClearSelection = () => {
    onSelectVehicle(null);
  };

  // Filter vehicles
  const filteredVehicles = vehicles.filter((v) => {
    // Extract nested vehicle data (admin endpoint has nested structure)
    const vehicleData = v.vehicle || v;
    const hostData = v.host;
    const pricingData = v.pricing;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const make = (vehicleData.make || vehicleData.vehicle_make || '').toLowerCase();
      const model = (vehicleData.model || vehicleData.vehicle_model || '').toLowerCase();
      const plate = (vehicleData.plate_number || vehicleData.plateNumber || '').toLowerCase();
      const hostName = (hostData?.name || hostData?.host_name || '').toLowerCase();

      const matchesSearch = make.includes(query) || model.includes(query) || plate.includes(query) || hostName.includes(query);
      if (!matchesSearch) return false;
    }

    // Make filter
    if (selectedMake && (vehicleData.make || vehicleData.vehicle_make) !== selectedMake) return false;

    // Vehicle type filter (economy, standard, comfort, luxury)
    if (selectedVehicleType) {
      // Admin endpoint returns: vehicleTypeCode (primary), vehicleTypeId, vehicleTypeName
      // Note: vehicleData.type is legacy field, use vehicleTypeCode instead
      const vehicleTypeCode = vehicleData.vehicleTypeCode || vehicleData.vehicle_type_code || vehicleData.typeCode || vehicleData.type_code || vehicleData.type;
      const vehicleTypeId = vehicleData.vehicleTypeId || vehicleData.vehicle_type_id || vehicleData.typeId;

      // Skip vehicles without type set (null values)
      if (!vehicleTypeCode && !vehicleTypeId) return false;

      // Match by code or ID
      const matches = vehicleTypeCode === selectedVehicleType || vehicleTypeId === selectedVehicleType;
      if (!matches) return false;
    }

    // Body type filter (sedan, suv, etc)
    if (selectedBodyType) {
      // Admin endpoint returns: bodyTypeCode, bodyTypeId, bodyTypeName
      const bodyTypeCode = vehicleData.bodyTypeCode || vehicleData.body_type_code || vehicleData.bodyCode || vehicleData.body_code;
      const bodyTypeId = vehicleData.bodyTypeId || vehicleData.body_type_id || vehicleData.bodyId;

      // Skip vehicles without body type set (null values)
      if (!bodyTypeCode && !bodyTypeId) return false;

      // Match by code or ID
      const matches = bodyTypeCode === selectedBodyType || bodyTypeId === selectedBodyType;
      if (!matches) return false;
    }

    // Location filter
    if (selectedLocation) {
      const location = hostData?.city || hostData?.state || hostData?.location;
      if (location !== selectedLocation) return false;
    }

    // Tier filter
    if (selectedTier) {
      const seatingCapacity = vehicleData.seating_capacity || vehicleData.seatingCapacity;
      const tier = vehicleData.tier || vehicleData.vehicle_tier || (seatingCapacity ? `${seatingCapacity} seats` : null);
      if (tier !== selectedTier) return false;
    }

    // Rental option filter - only show vehicles that offer the selected service
    if (rentalOption) {
      const isSelfDrive = rentalOption.startsWith('self_drive');
      const isWithDriver = rentalOption.startsWith('with_driver');

      if (isSelfDrive) {
        // For self-drive, check if selfDrive > 0 (admin endpoint uses 'selfDrive', not 'selfDriveRate')
        const selfDriveRate = pricingData?.selfDrive || pricingData?.selfDriveRate || pricingData?.self_drive_rate || vehicleData.selfDriveRate || vehicleData.self_drive_rate || 0;
        if (selfDriveRate <= 0) return false;
      }

      if (isWithDriver) {
        // For with-driver, check if withDriver > 0 (admin endpoint uses 'withDriver', not 'withDriverRate')
        const withDriverRate = pricingData?.withDriver || pricingData?.withDriverRate || pricingData?.with_driver_rate || vehicleData.withDriverRate || vehicleData.with_driver_rate || 0;
        if (withDriverRate <= 0) return false;
      }
    }

    // Note: No need to filter by hasAirportPricing here
    // For airport transfers, we fetch from dedicated endpoint with hasPricing=true
    // The backend already filters vehicles with airport pricing

    return true;
  });

  // Log filtering results
  useEffect(() => {
    // Filtering results tracked here
  }, [vehicles.length, filteredVehicles.length, searchQuery, selectedMake, selectedVehicleType, selectedBodyType, selectedLocation, selectedTier]);

  // Paginate
  const paginatedVehicles = filteredVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatCurrency = (amount: number) => {
    return `₦${(amount || 0).toLocaleString('en-NG')}`;
  };

  const getVehicleName = (v: any) => {
    // Handle nested structure from admin endpoint
    const vehicleData = v.vehicle || v;
    const make = vehicleData.make || vehicleData.vehicle_make || '';
    const model = vehicleData.model || vehicleData.vehicle_model || '';

    // Remove "Driver Service" prefix if present
    const cleanMake = make.replace(/^Driver Service\s*/i, '').trim();
    const cleanModel = model.replace(/^Driver Service\s*/i, '').trim();

    return `${cleanMake} ${cleanModel}`.trim() || 'Vehicle';
  };

  // Get vehicle type display name (Economy, Standard, Comfort, Luxury)
  const getVehicleTypeName = (v: any) => {
    const vehicleData = v.vehicle || v;

    // Best: Use the display name that's already in the vehicle data
    if (vehicleData.vehicleTypeName) return vehicleData.vehicleTypeName;

    // Good: Use the code field (vehicleTypeCode is more reliable than type)
    const typeCode = vehicleData.vehicleTypeCode || vehicleData.type;
    if (!typeCode) return null;

    // Look up in loaded metadata
    const matchingType = vehicleTypes.find(
      (t) => t.typeCode === typeCode || t.typeCode.toLowerCase() === typeCode.toLowerCase()
    );
    if (matchingType) return matchingType.typeName;

    // Fallback to capitalized code
    return typeCode.charAt(0).toUpperCase() + typeCode.slice(1);
  };

  // Get body type display name (Sedan, SUV, Truck, etc)
  const getBodyTypeName = (v: any) => {
    const vehicleData = v.vehicle || v;

    // Best: Use the display name that's already in the vehicle data
    if (vehicleData.bodyTypeName) return vehicleData.bodyTypeName;

    // Good: Use the code field
    const bodyCode = vehicleData.bodyTypeCode || vehicleData.bodyType;
    if (!bodyCode) return null;

    // Look up in loaded metadata
    const matchingBody = bodyTypes.find(
      (b) => b.bodyCode === bodyCode || b.bodyCode.toLowerCase() === bodyCode.toLowerCase()
    );
    if (matchingBody) return matchingBody.bodyName;

    // Fallback to capitalized code
    return bodyCode.charAt(0).toUpperCase() + bodyCode.slice(1);
  };

  return (
    <Box>
      {/* Selected Vehicle Display */}
      {selectedVehicle && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.main' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <DirectionsCar color="success" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body1" fontWeight={600}>
                {getVehicleName(selectedVehicle)}
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    const vehicleData = selectedVehicle.vehicle || selectedVehicle;
                    return vehicleData.plate_number || vehicleData.plateNumber;
                  })()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(() => {
                    // For airport transfers, show SPECIFIC price for selected airport+zone
                    if (bookingType === 'airport-transfer' && selectedVehicle.airportPricings?.length > 0 && airportId && zoneId) {
                      const pricings = selectedVehicle.airportPricings;
                      const selectedPricing = pricings.find((p: any) =>
                        p.airportId === airportId && p.zoneId === zoneId
                      );

                      if (selectedPricing) {
                        const pickupPrice = selectedPricing.pickupPrice || 0;
                        return `${formatCurrency(pickupPrice)}/trip`;
                      }
                    }

                    // For rentals, show daily rate
                    const pricingData = selectedVehicle.pricing;
                    const vehicleData = selectedVehicle.vehicle || selectedVehicle;
                    const selfDrivePrice = pricingData?.selfDrive || 0;
                    const withDriverPrice = pricingData?.withDriver || 0;
                    const price = Math.max(selfDrivePrice, withDriverPrice) ||
                                 pricingData?.dailyRate ||
                                 vehicleData.pricePerDay ||
                                 vehicleData.dailyRate ||
                                 0;
                    return `${formatCurrency(price)}/day`;
                  })()}
                </Typography>
                {(() => {
                  const hostData = selectedVehicle.host;
                  const hostName = hostData?.name || hostData?.host_name;
                  return hostName && (
                    <Typography variant="caption" color="text.secondary">
                      Host: {hostName}
                    </Typography>
                  );
                })()}
              </Stack>
            </Box>
            <Button size="small" onClick={handleClearSelection} disabled={disabled}>
              Change
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Select Vehicle Button (only show when no vehicle selected) */}
      {!selectedVehicle && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<DirectionsCar />}
          onClick={() => setOpen(true)}
          disabled={disabled}
          sx={{ py: 2 }}
        >
          {label} {required && '*'}
        </Button>
      )}

      {/* Vehicle Selection Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Select Vehicle</Typography>
            <IconButton onClick={() => setOpen(false)} size="small">
              <Clear />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    placeholder="Search by make, model, or host name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                  />
                </Grid>

                {/* Approval Status Filter (Backend filter - high priority) */}
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Approval Status</InputLabel>
                    <Select
                      value={selectedStatus}
                      label="Approval Status"
                      onChange={(e) => {
                        setSelectedStatus(e.target.value);
                        setPage(0); // Reset to first page when filter changes
                      }}
                    >
                      <MenuItem value="approved">Approved Only</MenuItem>
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending_approval">Pending</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="draft">Draft</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Fuel Type Filter (Backend filter) */}
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Fuel Type</InputLabel>
                    <Select
                      value={selectedFuelType}
                      label="Fuel Type"
                      onChange={(e) => {
                        setSelectedFuelType(e.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="">All Fuels</MenuItem>
                      <MenuItem value="petrol">Petrol</MenuItem>
                      <MenuItem value="diesel">Diesel</MenuItem>
                      <MenuItem value="electric">Electric</MenuItem>
                      <MenuItem value="hybrid">Hybrid</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Transmission Filter (Backend filter) */}
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Transmission</InputLabel>
                    <Select
                      value={selectedTransmission}
                      label="Transmission"
                      onChange={(e) => {
                        setSelectedTransmission(e.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="automatic">Automatic</MenuItem>
                      <MenuItem value="manual">Manual</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Make</InputLabel>
                    <Select
                      value={selectedMake}
                      label="Make"
                      onChange={(e) => setSelectedMake(e.target.value)}
                    >
                      <MenuItem value="">All Makes</MenuItem>
                      {makes.map((make) => (
                        <MenuItem key={make} value={make}>
                          {make}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={selectedVehicleType}
                      label="Type"
                      onChange={(e) => setSelectedVehicleType(e.target.value)}
                    >
                      <MenuItem value="">All Types</MenuItem>
                      {vehicleTypes.map((type) => (
                        <MenuItem key={type.id} value={type.typeCode}>
                          {type.typeName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Body</InputLabel>
                    <Select
                      value={selectedBodyType}
                      label="Body"
                      onChange={(e) => setSelectedBodyType(e.target.value)}
                    >
                      <MenuItem value="">All Bodies</MenuItem>
                      {bodyTypes.map((type) => (
                        <MenuItem key={type.id} value={type.bodyCode}>
                          {type.bodyName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={selectedLocation}
                      label="Location"
                      onChange={(e) => setSelectedLocation(e.target.value)}
                    >
                      <MenuItem value="">All Locations</MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location} value={location}>
                          {location}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Capacity</InputLabel>
                    <Select
                      value={selectedTier}
                      label="Capacity"
                      onChange={(e) => setSelectedTier(e.target.value)}
                    >
                      <MenuItem value="">All Capacities</MenuItem>
                      {tiers.map((tier) => (
                        <MenuItem key={tier} value={tier}>
                          {tier}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleClearFilters}
                    size="small"
                    sx={{ height: '40px' }}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {filteredVehicles.length} vehicle(s) found (out of {vehicles.length} total)
                {rentalOption && (
                  <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Showing only vehicles that offer{' '}
                    {rentalOption.startsWith('self_drive') ? 'self-drive' : 'with-driver'} service
                  </Typography>
                )}
                {bookingType === 'airport-transfer' && (
                  <Typography component="span" variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Showing only vehicles with airport transfer pricing configured
                  </Typography>
                )}
              </Typography>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Loading */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredVehicles.length === 0 ? (
            <Alert severity="info">No vehicles found matching the filters.</Alert>
          ) : (
            <>
              {/* Warning for non-approved vehicles */}
              {selectedStatus !== 'approved' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Showing Non-Approved Vehicles
                  </Typography>
                  <Typography variant="caption">
                    You are viewing vehicles with status: <strong>{selectedStatus === 'all' ? 'All Statuses' : selectedStatus.replace('_', ' ').toUpperCase()}</strong>.
                    Only APPROVED vehicles should be used for new bookings. Non-approved vehicles are shown for admin review purposes only.
                  </Typography>
                </Alert>
              )}

              {/* Vehicles Grid */}
              <Grid container spacing={2}>
                {paginatedVehicles.map((v) => {
                  // Extract nested data for display
                  const vehicleData = v.vehicle || v;
                  const hostData = v.host;
                  const pricingData = v.pricing;
                  const photosData = v.photos;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={v.id || vehicleData.vehicle_id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: 4,
                          },
                        }}
                      >
                        {/* Vehicle Image */}
                        <Box
                          sx={{
                            height: 180,
                            bgcolor: 'grey.200',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          {photosData?.items?.length > 0 ? (
                            <img
                              src={photosData.items[0].url || photosData.items[0]}
                              alt={getVehicleName(v)}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <DirectionsCar sx={{ fontSize: 80, color: 'grey.400' }} />
                          )}

                          {/* Approval Status Badge */}
                          {(() => {
                            // Backend returns status at top level: {id, status: 'approved', isActive, vehicle: {...}}
                            const status = v.status || 'unknown';
                            const badge = getStatusBadge(status);
                            return (
                              <Chip
                                label={badge.label}
                                size="small"
                                color={badge.color}
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  fontWeight: 700,
                                  fontSize: '0.7rem',
                                  boxShadow: 2,
                                }}
                              />
                            );
                          })()}
                        </Box>

                        <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                          {/* Vehicle Name */}
                          <Typography variant="h6" component="div" gutterBottom>
                            {getVehicleName(v)}
                          </Typography>

                          {/* Year & Tier */}
                          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                            <Chip
                              label={vehicleData.year || vehicleData.vehicle_year || 'N/A'}
                              size="small"
                              variant="outlined"
                            />
                            {(vehicleData.tier || vehicleData.vehicle_tier) && (
                              <Chip
                                label={vehicleData.tier || vehicleData.vehicle_tier}
                                size="small"
                                color="primary"
                              />
                            )}
                          </Stack>

                          {/* Plate Number */}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            <strong>Plate:</strong> {vehicleData.plate_number || vehicleData.plateNumber}
                          </Typography>

                          {/* Vehicle Type (Economy, Standard, Comfort, Luxury) */}
                          {getVehicleTypeName(v) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Class:</strong> {getVehicleTypeName(v)}
                            </Typography>
                          )}

                          {/* Body Type (Sedan, SUV, Truck, etc) */}
                          {getBodyTypeName(v) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Body:</strong> {getBodyTypeName(v)}
                            </Typography>
                          )}

                          {/* Location */}
                          {(hostData?.city || hostData?.state || hostData?.location) && (
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                              <LocationOn fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {hostData.city || hostData.state || hostData.location}
                              </Typography>
                            </Stack>
                          )}

                          {/* Host */}
                          {(hostData?.name || hostData?.host_name) && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              <strong>Host:</strong> {hostData.name || hostData.host_name}
                            </Typography>
                          )}

                          {/* Price */}
                          <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                            {(() => {
                              // For airport transfers, show airport pricing info
                              if (bookingType === 'airport-transfer' && v.airportPricings?.length > 0) {
                                const pricings = v.airportPricings;

                                // Find the pricing for the selected airport and zone
                                const selectedPricing = pricings.find((p: any) =>
                                  p.airportId === airportId && p.zoneId === zoneId
                                );

                                if (selectedPricing) {
                                  const pickupPrice = selectedPricing.pickupPrice || 0;

                                  if (pickupPrice > 0) {
                                    return (
                                      <>
                                        {formatCurrency(pickupPrice)}
                                        <Typography component="span" variant="caption" color="text.secondary">
                                          {' '}/trip
                                        </Typography>
                                      </>
                                    );
                                  }
                                }

                                // Fallback if no pricing found for selected zone (shouldn't happen if filtering works)
                                return (
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    Price not configured for this zone
                                  </Typography>
                                );
                              }

                              // For rentals, show daily rate
                              const selfDrivePrice = pricingData?.selfDrive || 0;
                              const withDriverPrice = pricingData?.withDriver || 0;
                              const price = Math.max(selfDrivePrice, withDriverPrice) ||
                                           pricingData?.dailyRate ||
                                           vehicleData.pricePerDay ||
                                           vehicleData.dailyRate ||
                                           0;
                              return (
                                <>
                                  {formatCurrency(price)}
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    /day
                                  </Typography>
                                </>
                              );
                            })()}
                          </Typography>
                        </CardContent>

                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => handleSelectVehicle(v)}
                          >
                            Select Vehicle
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Pagination */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Stack spacing={2} alignItems="center">
                  <Pagination
                    count={Math.ceil(filteredVehicles.length / rowsPerPage)}
                    page={page + 1}
                    onChange={(_, value) => setPage(value - 1)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Rows per page:
                    </Typography>
                    <Select
                      size="small"
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(0);
                      }}
                    >
                      <MenuItem value={6}>6</MenuItem>
                      <MenuItem value={12}>12</MenuItem>
                      <MenuItem value={24}>24</MenuItem>
                      <MenuItem value={48}>48</MenuItem>
                    </Select>
                  </Stack>
                </Stack>
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleSelector;
